import { Injectable, Injector } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpService } from './http-service.service';
import { LocalStorageService } from './local-storage.service';
import { DataTransferService } from './data-transfer.service';
import { environment } from 'projects/customer/src/environments/environment';
import { PermissionService } from './permission.service';
import { ChurnZeroService } from './churn-zero.service';
import { clearCsrfToken, storeCsrfToken } from '../utils/session-cookie.util';
import { JD_SELLER_PC_TYPE } from '../utils/jd-seller';

@Injectable({
    providedIn: 'root'
})
export class AuthenticationService {
    constructor(
        public httpService: HttpService,
        private localStorageService: LocalStorageService,
        private injector: Injector,
        private dataTransferService: DataTransferService,
        private churnZeroService: ChurnZeroService
    ) {}

    public getLoginAccess(param) {
        const path = 'frontend/v1/login';
        return this.httpService.callNoAuthPostApi(path, param);
    }

    public verifyOtp(param: { user_id: number; otp: string }) {
        const path = 'frontend/v1/login/verify_otp';
        return this.httpService.callNoAuthPostApi(path, param);
    }

    public resendOtp(param: { user_id: number }) {
        const path = 'frontend/v1/login/resend_otp';
        return this.httpService.callNoAuthPostApi(path, param);
    }

    public sendOrderOtp(param: { user_id: number; email: string }) {
        const path = 'frontend/v1/login/send_otp';
        return this.httpService.callAuthPostApi(path, param);
    }

    public verifyOrderOtp(param: {
        user_id: number;
        otp: string;
        is_order?: boolean;
        order_id?: number | string;
    }) {
        const path = 'frontend/v1/login/verify_otp';
        return this.httpService.callAuthPostApi(path, param);
    }


    public getSsoLoginUrl(param) {
        const path = 'frontend/v1/sso/login_url';
        return this.httpService.callNoAuthPostApi(path, param);
    }

    public getSsoCallback(param) {
        const path = 'frontend/v1/sso/callback';
        return this.httpService.callNoAuthPostApi(path, param);
    }

    public Logout() {
        const path = 'frontend/v1/logout';
        const param = {};
        return this.httpService.callPutApi(path, param);
    }

    public createSession(userData, isFromRetailer?) {
        userData.data.language = {
            code: 'en'
        };
        this.localStorageService.set('userData', userData.data);
        this.localStorageService.set(
            'isAsendiaUser',
            userData.data.pc_type == 2 ? true : false
        );
        this.localStorageService.set(
            'isAsendiaUserUk',
            userData.data.pc_type == 9 ? true : false
        );
        this.localStorageService.set(
            'windsorEnabled',
            userData.data.is_windsor_enabled == true
        );
        this.localStorageService.set(
            'isCameraEvidenceEnabled',
            userData.data.is_camera_evidence_enabled == 1
        );
        this.localStorageService.set(
            'isGrowmoreLogistic',
            userData?.data?.tenant_id == environment.growMoreTenantId
                ? true
                : false
        );

        const isDpdLogistic = environment.dpdTenantId.some(
            (dpdId) => userData?.data?.tenant_id === dpdId
        );
        this.localStorageService.set('isDpdLogistic', isDpdLogistic);
        // Jingdong (JDL) tenant: pc_type 10
        this.localStorageService.set(
            'isJdlTenant',
            userData.data.pc_type == JD_SELLER_PC_TYPE
        );

        const enableHoldPotFeature =
            userData?.data?.enable_hold_pot_feature === 1 ||
            userData?.data?.enable_hold_pot_feature === true ||
            userData?.data?.enable_hold_pot_feature === '1';
        this.localStorageService.set(
            'enableHoldPotFeature',
            enableHoldPotFeature
        );

        //   const isDpdLogistic = environment.dpdTenantId.some(
        //     (dpdId) => userData?.data?.tenant_id === dpdId
        // );
        const isTranscorpLogistic = environment.transcorpTenantId.some(
            (transcorpId) => userData?.data?.tenant_id === transcorpId
        );
        this.localStorageService.set(
            'isTranscorpLogistic',
            isTranscorpLogistic
        );

        const isSwiftHub = environment.swiftHubTenantId.some(
            (isSwiftHubId) => userData?.data?.tenant_id === isSwiftHubId
        );
        this.localStorageService.set('isSwiftHub', isSwiftHub);
        this.localStorageService.set(
            'auto_populate_sender_address',
            userData?.data?.auto_populate_sender_address
        );

        this.localStorageService.set(
            'customerTypeId',
            userData?.data?.customer_type?.type_id
        );
        this.localStorageService.set(
            'orderType',
            userData?.data?.orderType ?? null
        );
        this.localStorageService.set(
            'first_login',
            userData?.data?.first_login ? userData?.data?.first_login : false
        );
        this.localStorageService.set(
            'is2faFeature',
            !!userData?.data?.is_2fa_feature
        );
        this.localStorageService.set(
            'tenantIs2faEnabled',
            !!userData?.data?.tenant_is_2fa_enabled
        );
        this.localStorageService.set(
            'customerIs2faEnabled',
            !!userData?.data?.customer_is_2fa_enabled
        );

        const customer_id = environment.multiLingualCustomerId.some(
            (customerId) => userData?.data?.customer_id === customerId
        );
        this.localStorageService.set('isMultilingualSupport', customer_id);

        /*
         * The session JWT is deliberately not stored here any more. The API returns it
         * in an HttpOnly cookie that JavaScript cannot read, which is what stops an XSS
         * payload from stealing it. Everything kept below is non secret profile data
         * that the UI needs in order to render.
         *
         * The CSRF token does have to be kept: the API is on a different host, so its
         * cookies are not readable from here, and state changing requests have to echo
         * this value back in a header.
         */
        storeCsrfToken(userData?.data?.csrf_token);

        this.localStorageService.set(
            'permissionArray',
            userData?.data?.permissions
        );

        const permissionService = this.injector.get(PermissionService);
        permissionService.initializePermissions(userData?.data?.permissions);
        if (isFromRetailer) {
            this.dataTransferService.updateRefreshFromRetailer(null);
        }
        this.churnZeroService.initialize();
    }

    public clearSession() {
        const permissionService = this.injector.get(PermissionService);
        permissionService.flushPermission();
        //explicit, so the CSRF token cannot survive a future narrowing of clear()
        clearCsrfToken();
        this.localStorageService.clear();
    }

    /*
     * The session cookie is invisible to JavaScript, so "am I signed in" is answered
     * by the presence of the profile data that login stores instead of by the token.
     */
    public hasActiveSession(): boolean {
        return !!this.localStorageService.get('userData');
    }

    public resetPassword(params) {
        const path = 'api/reset_password';
        return this.httpService.callAuthPostApi(path, params);
    }

    public changePassword(params) {
        // const path = 'reset_password';
        const path = 'frontend/v1/login/set_password';
        return this.httpService.callPutApi(path, params);
    }
    public confirmEmail(token) {
        const path = 'api/users/confirmation?confirmation_token=' + token;
        return this.httpService.callAuthGetApi(path, null, 'noAuth');
    }
    public loadCapabilities() {
        const path = 'api/v1/users/capabilities';
        return this.httpService.callAuthGetApi(path);
    }

    public forgotPassword(emailID) {
        const path = `frontend/v1/login/${emailID}/forgot_password`;
        return this.httpService.callAuthGetApi(path, null, 'noAuth');
    }

    public setPassword(params) {
        const path = 'frontend/v1/login/reset_password';
        return this.httpService.callPutApi(path, params);
    }

    getLogoImage(url) {
        const path = 'v1/masters/landing_page_info?domain_name=' + url;
        return this.httpService.callAuthGetMasterApi(path);
    }
}
